import { styled } from '@styledComponents';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { Text } from '@components/typography';
import { MediumBoldTextStyles, SmallBoldTextStyles, Label2 } from '@components/typography/interVariants';
import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { ButtonNoneStyled } from '@components/ButtonNoneStyled';

const SPACE = '1.25rem';

export interface ItemPerLine {
  sm: number;
  belowSm: number;
}

export const GridContainer = styled(Box)<{ itemPerLine: ItemPerLine }>`
  display: grid;
  gap: ${themeSpace(8)};
  padding: ${SPACE};
  max-width: 1800px;

  ${themeMediaQuery('sm')} {
    grid-template-columns: repeat(${({ itemPerLine }) => itemPerLine.sm}, minmax(0px, 1fr));

    > div:not(:nth-child(${({ itemPerLine }) => itemPerLine.sm}n))::before {
      content: ' ';
    }

    > div:not(:nth-last-child(-n + ${({ itemPerLine }) => itemPerLine.sm}))::after {
      content: ' ';
    }
  }

  ${themeMediaQuery('below_sm')} {
    gap: ${themeSpace(5)};
    padding: ${themeSpace(5)};
    grid-template-columns: repeat(${({ itemPerLine }) => itemPerLine.belowSm}, minmax(0px, 1fr));

    > div:not(:nth-child(${({ itemPerLine }) => itemPerLine.belowSm}n))::before {
      content: ' ';
    }

    > div:not(:nth-last-child(-n + ${({ itemPerLine }) => itemPerLine.belowSm}))::after {
      content: ' ';
    }
  }
`;

export const GridItemContainer = styled(Flex)`
  flex-direction: column;
  position: relative;
  align-items: center;
`;

export const ItemWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  position: relative;

  &::before {
    background-color: ${themeColor('mono4')};
    height: calc(100% + ${themeSpace(8)});
    position: absolute;
    right: -${SPACE};
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
  }

  &::after {
    background-color: ${themeColor('mono4')};
    bottom: -${SPACE};
    height: 1px;
    position: absolute;
    width: 100%;
  }

  &:focus,
  &:hover {
    outline: none;
    position: relative;
    top: -${SPACE};
    left: -${SPACE};
    width: calc(100% + ${themeSpace(8)});
    padding: 0;

    &::before {
      right: 0;
      top: calc(50% + ${SPACE});
    }

    ${GridItemContainer} {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      padding: ${SPACE} ${SPACE} 0px ${SPACE};
      z-index: 1;
      background: white;
      box-shadow: rgb(0 0 0 / 23%) 0px 10px 30px 0px;
      border-radius: ${themeSpace(3)};
      height: calc(100% + ${themeSpace(8)});
    }
  }

  ${themeMediaQuery('below_sm')} {
    padding-bottom: ${themeSpace(5)};

    &::before {
      height: calc(100% + ${themeSpace(5)});
      right: ${themeSpace(-3)};
    }

    &::after {
      bottom: ${themeSpace(-3)};
    }

    &:hover {
      top: ${themeSpace(-3)};
      left: ${themeSpace(-3)};
      width: calc(100% + ${themeSpace(5)});

      &::before {
        top: calc(50% + ${themeSpace(3)});
      }

      ${GridItemContainer} {
        padding: ${themeSpace(3)} ${themeSpace(3)} 0px ${themeSpace(3)};
        height: calc(100% + ${themeSpace(5)});
      }
    }
  }
`;

export const GridItem = styled(ButtonNoneStyled)`
  white-space: initial;
  display: block;
`;

export const ProductTile = styled(Box)`
  width: 100%;
  contain: content;
  position: relative;
`;

export const ImageWrapper = styled(Flex)`
  width: 100%;
  overflow: hidden;
  border-radius: ${themeSpace(1)};
  position: relative;
`;

export const ImageContainer = styled(Flex)`
  overflow: hidden;
  position: relative;
  width: 100%;
  padding-top: 100%;
`;

export const ProductPhoto = styled.img`
  width: 100%;
  height: 100%;
  mix-blend-mode: multiply;
  transition: transform 0.25s ease 0s;
  position: absolute;
  top: 0px;
  background-color: lightgray;
`;

export const StyledFlex = styled(Flex)`
  min-height: 96px;
  ${themeMediaQuery('below_sm')} {
    min-height: 90px;
  }
`;

export const BrandText = styled(Text)`
  ${Label2};
  color: #767676;
  margin-bottom: ${themeSpace(1)};
  ${themeMediaQuery('below_sm')} {
    margin-bottom: ${themeSpace(3)};
  }
`;

export const TitleText = styled(Text)`
  ${MediumBoldTextStyles};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  ${themeMediaQuery('below_sm')} {
    ${SmallBoldTextStyles};
  }
`;

export const HiddenText = styled.span`
  display: none;
`;
